import { action } from 'mobx'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { type OpportunityOffer } from './OpportunityOffer'
import { formatWithLegendPriceUnit } from '@/features/jobs/legends/legend-price-unit'
import { task } from 'mobx-task'
import { type OpportunityOfferStore } from './OpportunityOfferStore'
import type { PickupConsultationJobFn } from '@/features/jobs'
import {
  type ConsultationModalViewModel,
  type ConsultationDetails,
} from '@/features/opportunities/ConsultationModal/ConsultationModalViewModel'
import { Id } from '@/infra/id'
import type { NavigateFn } from '@/RootStore'
import { links } from '@/utils/link'
import type { AlertFactory } from '@/features/shell'
import type { GetCurrentMemberFn } from '@/features/iam'

/**
 * View Model for the Offer Modal.
 */
export class OfferModalViewModel {
  readonly modal = new ModalViewModel()

  constructor(
    private readonly _opportunityOfferStore: OpportunityOfferStore,
    private readonly _opportunityOffer: OpportunityOffer,
    private readonly _consultationModalViewModel: ConsultationModalViewModel,
    private readonly _pickupConsultationJob: PickupConsultationJobFn,
    private readonly _navigate: NavigateFn,
    private readonly _newAlert: AlertFactory,
    private readonly _getCurrentMember: GetCurrentMemberFn,
  ) {}

  /**
   * The ID of the opportunity.
   */
  get id() {
    return this._opportunityOffer.id
  }

  /**
   * The name of the workspace where the offer exists.
   */
  get workspaceName() {
    return this._opportunityOffer.workspace.name
  }

  /**
   * The display name for the job.
   */
  get displayName() {
    return this._opportunityOffer.displayName
  }

  /**
   * The first and last name of the job's client.
   */
  get championClientName() {
    return this._opportunityOffer.client.displayName
  }

  /**
   * The provider cut amount for the job.
   */
  get proCut() {
    const amount = this._opportunityOffer.providerCutAmount
    return amount > 0 ? formatWithLegendPriceUnit(undefined, amount) : ''
  }

  /**
   * The job specs, as one line.
   */
  get jobSpecs() {
    return this._opportunityOffer.specs.join(' • ')
  }

  /**
   * Indicates what action will be taken upon action expiration.
   */
  get offerExpirationAction() {
    return this._opportunityOffer.expirationAction
  }

  /**
   * The time until offer expiration.
   */
  get timeUntilExpiration() {
    return this._opportunityOffer.timeUntilExpiration ?? 0
  }

  /**
   * The date at which the offer expires, in milliseconds.
   */
  get expirationMillis() {
    return this._opportunityOffer.expirationMillis
  }

  /**
   * Closes the offer modal as well as the consultation modal if it's showing.
   */
  @action.bound
  close() {
    this._consultationModalViewModel.close()
    this.modal.close()
  }

  /**
   * Accepts the given opportunity.
   */
  acceptOpportunity = task.resolved(() => this._acceptOpportunity())

  /**
   * Declines the given opportunity.
   */
  declineOpportunity = task.resolved(async () => {
    await this._opportunityOfferStore
      .declineOffer(this._opportunityOffer.id)
      .then(this.close)
  })

  private async _acceptOpportunity() {
    const consultation = this._opportunityOffer.consultation

    // Just accept the offer when there is no associated consultation.
    if (!consultation) {
      await this._opportunityOfferStore
        .acceptOffer(this._opportunityOffer.id)
        .then(this.close)
        .then(this._afterOfferAccepted)

      return
    }

    // Just accept the offer when there is a consultation, but no selected times.
    if (
      !consultation.availabilityTimes ||
      consultation.availabilityTimes.length === 0
    ) {
      await this._opportunityOfferStore
        .acceptOffer(this._opportunityOffer.id)
        .then(this.close)
        .then(this._afterOfferAccepted)

      return
    }

    const details: ConsultationDetails = {
      requestedLength: consultation.requestedLength,
      availability: consultation.availabilityTimes,
      bookedConsultations: [],
      client: this._opportunityOffer.client.user,
    }

    // prettier-ignore
    const bookedTime = await this
      ._consultationModalViewModel
      .promptForTimeSlots(details, this._currentMember())

    if (bookedTime) {
      await this._pickupConsultationJob(this._opportunityOffer.id, bookedTime)
        .then(this.close)
        .then(this._afterOfferAccepted)
    }
  }

  private _currentMember() {
    return this._getCurrentMember(this._opportunityOffer.workspace.id)
  }

  /**
   * Actions to take after an offer has been accepted.
   */
  private readonly _afterOfferAccepted = () => {
    this._newAlert()
      .withState('success')
      .saying('You accepted an offer.', 'Click to start work on your job.')
      .autoDismiss()
      .onInteract(() => {
        this._navigate(links.job(Id.assert(this._opportunityOffer.id)))
      })
      .show()
  }
}
